import React from "react";
import tinycolor from "tinycolor2";
import { Translations } from "../context/TranslationContext";
import { TabId } from "../services/api/types/portalConfiguration/translations";
import { CustomisationData, PageUid } from "../context/CustomisationContext";

export const positiveNumberFieldProps = {
  inputProps: { min: 0 },
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "+") e.preventDefault();
  },
};

export const getInitials = (firstName: string, lastName: string) => {
  const f = firstName?.trim()?.[0] ?? "";
  const l = lastName?.trim()?.[0] ?? "";
  return (f + l).toUpperCase();
};

export const truncateName = (name: string): string => {
  if (!name) return "";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return name;
  return `${words[0]} ${words[1]}…`;
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Resolves asset URLs (e.g. from the backend) so they always load from the
 * site origin. Relative paths like "assets/123/logo.png" are resolved against
 * window.location.origin, avoiding incorrect paths when the app is open on
 * deep routes (e.g. /portal-configuration/xyz would otherwise become
 * /portal-configuration/xyz/assets/123/logo.png).
 */
export const resolveAssetUrl = (url: string | undefined | null): string => {
  if (url == null || url === "") return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
};

export const setFavicon = (url: string) => {
  const link: HTMLLinkElement =
    document.querySelector("link[rel*='icon']") ||
    document.createElement("link");
  link.type = "image/png";
  link.rel = "shortcut icon";
  link.href = resolveAssetUrl(url) || url;
  document.getElementsByTagName("head")[0].appendChild(link);
};

export const getShadeColour = (baseColor: string, amount: number = 10) => {
  const color = tinycolor(baseColor);
  const isDark = color.isDark();
  return isDark
    ? color.lighten(amount).toHexString()
    : color.darken(amount).toHexString();
};

export const isAllEmptyStrings = (obj: any): boolean => {
  if (typeof obj === "string") return obj === "";
  if (typeof obj !== "object" || obj === null) return false;

  return Object.values(obj).every((value) => isAllEmptyStrings(value));
};

export const toCamelCase = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase());
};

export const fromCamelCase = (str: string) => {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase());
};

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const hasSomeTranslations = (
  translations: Translations,
  tabId: TabId,
): boolean => {
  const tab = translations[tabId];
  if (!tab) return false;

  return Object.values(tab).some(
    (section) =>
      section && Object.values(section).some((value) => value !== ""),
  );
};

export const hasSomeCustomisation = (
  customisations: CustomisationData,
  pageUid: PageUid,
): boolean => {
  const page = customisations[pageUid];
  if (!page) return false;

  return Object.values(page).some(
    (section) =>
      section && Object.values(section).some((value) => value !== ""),
  );
};

export const getFileNameFromUrl = (url: string, index = 1) => {
  const parts = url.split("/");
  const requiredPart = parts[parts.length - index];
  return requiredPart?.replace(/\.[^/.]+$/, "");
};

export const hexToRgb = (hex: string): string => {
  let cleaned = hex.replace("#", "");
  if (cleaned.length === 3) {
    cleaned = cleaned
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r},${g},${b}`;
};

export const numberToOrdinalWord = (n: number) => {
  const words = [
    "Zero",
    "First",
    "Second",
    "Third",
    "Fourth",
    "Fifth",
    "Sixth",
    "Seventh",
    "Eighth",
    "Ninth",
    "Tenth",
    "Eleventh",
    "Twelfth",
    "Thirteenth",
    "Fourteenth",
    "Fifteenth",
    "Sixteenth",
    "Seventeenth",
    "Eighteenth",
    "Nineteenth",
  ];
  const tens = [
    "",
    "",
    "Twentieth",
    "Thirtieth",
    "Fortieth",
    "Fiftieth",
    "Sixtieth",
    "Seventieth",
    "Eightieth",
    "Ninetieth",
  ];

  if (n < 20) return words[n];
  const ten = Math.floor(n / 10);
  const unit = n % 10;

  return unit === 0 ? tens[ten] : `${tens[ten].slice(0, -2)}y-${words[unit]}`;
};

export const formatDateOnly = (dateString: string): string => {
  if (!dateString || dateString === "No due date") return dateString;

  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (error) {
    return dateString;
  }
};

export const formatAttemptDateRange = (
  started: string | null | undefined,
  completed: string | null | undefined,
): string => {
  if (!started) return "-";
  try {
    const startDate = new Date(started);
    if (isNaN(startDate.getTime())) return started;

    const dateStr = startDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    const startTime = startDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (!completed) return `${dateStr} · ${startTime}`;
    const endDate = new Date(completed);
    if (isNaN(endDate.getTime())) return `${dateStr} · ${startTime}`;

    const endTime = endDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const sameDay =
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getDate() === endDate.getDate();

    if (sameDay) {
      return `${dateStr} · ${startTime} - ${endTime}`;
    }

    const endDateStr = endDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return `${dateStr} · ${startTime} - ${endDateStr} · ${endTime}`;
  } catch {
    return started;
  }
};

export const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";

  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;

  if (bytes >= gb) {
    const value = bytes / gb;
    return `${value % 1 === 0 ? value : value.toFixed(1)} GB`;
  }

  if (bytes >= mb) {
    const value = bytes / mb;
    return `${value % 1 === 0 ? value : value.toFixed(1)} MB`;
  }

  if (bytes >= kb) {
    return `${Math.round(bytes / kb)} KB`;
  }

  return `${bytes} B`;
};

export const toBase64Utf8 = (value: string): string => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    for (let j = 0; j < chunk.length; j++) {
      binary += String.fromCharCode(chunk[j]);
    }
  }

  return btoa(binary);
};
